import {useState} from "react";
import {toast} from "react-hot-toast";

import {ServiceVariableGroupByCredential, ServiceVariableNoServiceInput} from "@/lib/types/serviceVariables.ts";
import {GetVariableNamesFromServiceVariableGroupByCredential} from "@/pages/checks/checkVaultServiceVariables/serviceVariableUtils.ts";

export const useNewCheckVaultItems = (
  initialCheckId: number | undefined,
  initialServiceVariables: ServiceVariableGroupByCredential[] = []
): {
  vaultItems: ServiceVariableGroupByCredential[];
  updateVaultServiceVariableName: (
    credentialId: string,
    serviceVariableProperty: string,
    serviceVariableName: string
  ) => boolean;
  setVaultItems: (
    value:
      | ((prevState: ServiceVariableGroupByCredential[]) => ServiceVariableGroupByCredential[])
      | ServiceVariableGroupByCredential[]
  ) => void;
  addVaultItem: (item: ServiceVariableGroupByCredential) => void;
  setCheckId: (value: ((prevState: number | undefined) => number | undefined) | number | undefined) => void;
  getServiceVariableInputFromVaultItems: () => ServiceVariableNoServiceInput[];
  deleteVaultItem: (id: string) => void;
  checkId: number | undefined;
} => {
  const [checkId, setCheckId] = useState<number | undefined>(initialCheckId);
  const [vaultItems, setVaultItems] = useState<ServiceVariableGroupByCredential[]>(initialServiceVariables);

  const deleteVaultItem = (id: string) => {
    setVaultItems((previousVaultItems) => previousVaultItems.filter((vaultItem) => vaultItem.credential.id !== id));
  };

  const addVaultItem = (item: ServiceVariableGroupByCredential) => {
    if (vaultItems.some((vaultItem) => vaultItem.credential.id === item.credential.id)) {
      toast.error("Credential already added to Check");
      return;
    }
    setVaultItems((previousVaultItems) => [...previousVaultItems, item]);
  };

  const updateVaultServiceVariableName = (
    credentialId: string,
    serviceVariableProperty: string,
    serviceVariableName: string
  ) => {
    const variableNames = GetVariableNamesFromServiceVariableGroupByCredential(vaultItems);
    if (variableNames.includes(serviceVariableName)) {
      toast.error("Variable name already exists");
      return false;
    }

    const vaultItemIndex = vaultItems.findIndex(
      (serviceVariableGroupByCredential) => serviceVariableGroupByCredential.credential.id === credentialId
    );
    const credentialGroup = vaultItems[vaultItemIndex];
    const serviceVariableIndex = credentialGroup.service_variables.findIndex(
      (serviceVariable) => serviceVariable.property_name === serviceVariableProperty
    );
    const serviceVariable = credentialGroup.service_variables[serviceVariableIndex];
    serviceVariable.variable_name = serviceVariableName;
    credentialGroup.service_variables[serviceVariableIndex] = serviceVariable;
    vaultItems[vaultItemIndex] = credentialGroup;
    setVaultItems([...vaultItems]);

    return true;
  };

  const getServiceVariableInputFromVaultItems = (): ServiceVariableNoServiceInput[] => {
    const serviceVariablesInput: ServiceVariableNoServiceInput[] = [];
    vaultItems.forEach((serviceVariableGroupByCredential) => {
      const groupServiceVariablesInput = serviceVariableGroupByCredential.service_variables.map((serviceVariable) => {
        return {
          credential_id: parseInt(serviceVariableGroupByCredential.credential.id),
          variable_name: serviceVariable.variable_name,
          property_name: serviceVariable.property_name,
        };
      });
      serviceVariablesInput.push(...groupServiceVariablesInput);
    });

    return serviceVariablesInput;
  };

  return {
    vaultItems,
    setVaultItems,
    deleteVaultItem,
    addVaultItem,
    updateVaultServiceVariableName,
    checkId,
    setCheckId,
    getServiceVariableInputFromVaultItems,
  };
};
